import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import type { MenuItem } from 'primereact/menuitem';
import { useContext, useEffect, useRef } from 'react';
import { MdAddCircle, MdDoneAll, MdOutlineDoneAll, MdHome, MdOutlineHome, MdInsertInvitation, MdOutlineInsertInvitation } from 'react-icons/md';
import { routeWithId } from '@/constants/routes';
import { Column, Row } from '@/components/container/Container';
import clsx from 'clsx';
import { X4bActivitySection } from '../engine/ConciergeCardViewModel';
import { CardContext } from '../context/CardContext';
import CreateNewRideMenu from './CreateNewRideMenu';
import { useAuthenticationStore } from '@/store';
import { X4bUserRoleEnum } from '@/constants/roles';

const ConciergeSidebar = () => {
	const createNewMenuRef = useRef<Menu>(null);
	const location = useLocation();
	const navigate = useNavigate();
	const fullPath = location.pathname + location.search;
	const { setSelectedCard } = useContext(CardContext);
	const isManagerOrUser = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.User, X4bUserRoleEnum.Manager]);

	const sectionRoute = (status: X4bActivitySection) => routeWithId('/concierge/statuses/:id', status);

	const itemRenderer = (item: MenuItem) => {
		const isSelected = fullPath === item.url;

		return (
			<Link
				to=""
				onClick={(e) => {
					e.preventDefault();

					if (!isSelected && item?.url) {
						setSelectedCard(null);
						navigate(item.url);
					}
				}}
			>
				<Row className={clsx(
					'pl-4 pr-6 h-10 gap-3',
					'font-poppins rounded-full items-center',
					'transition-all duration-300 ease-in-out',
					'hover:text-label-semibold hover:text-secondary60 hover:bg-primary99',
					{
						'bg-primary99': isSelected, 'bg-transparent': !isSelected,
						'text-label-semibold': isSelected, 'text-label-small2': !isSelected,
						'text-secondary60': isSelected, 'text-neutralVariant20': !isSelected,
					}
				)}>
					{item.icon || <div className='flex-none w-6 h-6' />}
					{item.label}
				</Row>
			</Link>
		);
	};

	const items: MenuItem[] = [
		{
			url: sectionRoute(X4bActivitySection.TodaysActivity), template: itemRenderer, id: 'Today’s Activity', label: 'Today’s Activity',
			icon: fullPath === sectionRoute(X4bActivitySection.TodaysActivity)
				? <MdHome size={24} className='flex-none text-primary60' />
				: <MdOutlineHome size={24} className='flex-none text-primary60' />
		},
		{ url: sectionRoute(X4bActivitySection.NeedsAttention), template: itemRenderer, id: 'Needs Attention', label: 'Needs Attention' },
		{ url: sectionRoute(X4bActivitySection.Requesting), template: itemRenderer, id: 'Requesting', label: 'Requesting' },
		{ url: sectionRoute(X4bActivitySection.AwaitingPickup), template: itemRenderer, id: 'Awaiting Pickup', label: 'Awaiting Pickup' },
		{ url: sectionRoute(X4bActivitySection.InProgress), template: itemRenderer, id: 'In Progress', label: 'In Progress' },
		{ url: sectionRoute(X4bActivitySection.Completed), template: itemRenderer, id: 'Completed', label: 'Completed' },
		{ url: sectionRoute(X4bActivitySection.Canceled), template: itemRenderer, id: 'Canceled', label: 'Canceled' },
		{
			url: sectionRoute(X4bActivitySection.Scheduled), template: itemRenderer, id: 'Scheduled', label: 'Scheduled',
			icon: fullPath === sectionRoute(X4bActivitySection.Scheduled)
				? <MdInsertInvitation size={24} className='flex-none text-primary60' />
				: <MdOutlineInsertInvitation size={24} className='flex-none text-primary60' />
		},
		{
			url: sectionRoute(X4bActivitySection.PastActivity), template: itemRenderer, id: 'Past Activity', label: 'Past Activity',
			icon: fullPath === sectionRoute(X4bActivitySection.PastActivity)
				? <MdDoneAll size={24} className='flex-none text-primary60' />
				: <MdOutlineDoneAll size={24} className='flex-none text-primary60' />
		},
	];

	return (
		<>
			<CreateNewRideMenu ref={createNewMenuRef} className="w-[180px]" />

			<Column className="px-3 pt-9 gap-11 items-start">
				{isManagerOrUser &&
					<Button
						id="concierge-create-new"
						className='h-14 py-4 pl-4 pr-5 gap-3 rounded-2xl bg-accent90 text-label-large2 text-accent20'
						label="Create New"
						icon={<MdAddCircle size={24} className="text-primary60" />}
						onClick={(e) => createNewMenuRef.current?.toggle(e)}
					/>
				}
				<Menu model={items} />
			</Column>
		</>
	);
};

export default ConciergeSidebar;
